import { useEffect, useState } from 'react';

import RichText from 'components/shared/richtext';
import renderSections from 'components/sections/render';
import useSWR from 'swr';

import RelatedProducts from 'components/sections/relatedProducts/relatedProducts';
import Expander from 'components/shared/expander/expander';
import getShopifyData from 'lib/shopify/getShopifyData';
import EvesReviews from 'components/shared/eves-reviews';
import { useGlobalModules } from 'contexts';
import Breadcrumbs from 'components/layout/breadcrumbs';
import useProductViewItemTracking from 'hooks/tracking/useProductViewItemTracking';
import styles from './productBundle.module.scss';
import ProductBundleInfo from './productBundleInfo';
import ProductGallery from '../product/productGallery/productGallery';

const PageProductBundle = ({ data, locale }) => {
  const [shopifyData, setShopifyData] = useState(null);
  const [bundlePrices, setBundlePrices] = useState({});

  const {
    thumbnailImage,
    products,
    additionalSections,
    productMedia,
    shopifyData: initialShopifyData,
  } = data;

  const globalModules = useGlobalModules();
  const { ingredientsTitle, disclaimerText, directionOfUseTitle, reviewsTitle } =
    globalModules.translations.product;

  const otherSections = additionalSections.filter(
    section => section.type !== 'SectionRelatedProducts'
  );
  const relatedProductsSection = additionalSections.find(
    section => section.type === 'SectionRelatedProducts'
  );

  const reviewProducts = products?.filter(product => !product.slug.includes('dispenser'));

  const fetcher = async () => {
    const bundleProductId = `gid://shopify/Product/${initialShopifyData?.variants?.[0]?.product_id}`;

    const actualShopifyData = await getShopifyData({
      productIds: [bundleProductId],
      locale,
    });
    return actualShopifyData?.[0];
  };

  const swrOptions = {
    refreshInterval: 60000,
  };

  const { data: updatedBundleData } = useSWR(initialShopifyData, fetcher, swrOptions);

  useEffect(() => {
    // set the initial prices from Contentful data
    if (!initialShopifyData) return;
    const initialData = {
      price: initialShopifyData?.variants?.[0]?.price,
      compareAtPrice: initialShopifyData?.variants?.[0]?.compareAtPrice,
      outOfStock: initialShopifyData?.variants?.[0]?.outOfStock,
      currencyCode: initialShopifyData?.variants?.[0]?.currencyCode,
    };
    setBundlePrices(initialData);
    setShopifyData(initialShopifyData);
  }, [initialShopifyData]);

  useEffect(() => {
    // update the prices from shopify data
    if (!updatedBundleData) return;
    const updatedPrices = {
      price: updatedBundleData.productPrice,
      compareAtPrice: updatedBundleData.productCompareAtPrice,
      outOfStock: updatedBundleData.outOfStock,
      currencyCode: updatedBundleData.currencyCode,
    };
    setBundlePrices(updatedPrices);
    setShopifyData({
      variants: [
        {
          price: updatedBundleData.productPrice,
          compareAtPrice: updatedBundleData.productCompareAtPrice,
          outOfStock: updatedBundleData.outOfStock,
          currencyCode: updatedBundleData.currencyCode,
          product_id: updatedBundleData.productId,
          id: initialShopifyData?.variants?.[0]?.id,
        },
      ],
    });
  }, [updatedBundleData]);

  useEffect(() => {
    if (shopifyData) {
      const { items: productSpecs } = data?.productSpecsCollection;
      const productType = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Producttype' ||
          type.filterType === 'Type de produit' ||
          type.filterType === 'Product type'
      );
      const productCategory = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Productcategorie' ||
          type.filterType === 'Catégorie de produit' ||
          type.filterType === 'Product Category'
      );
      const productSubCategory = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Subcategorie' ||
          type.filterType === 'Sous-catégorie' ||
          type.filterType === 'Sub Category'
      );

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductViewItemTracking({
        product: {
          name: data.title,
          id: shopifyData.variants?.[0]?.product_id,
          category: productType?.value,
          category2: productCategory?.value,
          category3: productSubCategory?.value,
          price: bundlePrices.price,
          outOfStock: Number(!bundlePrices.outOfStock), // negation outOfStock to convert to in_stock for tracking
          currencyCode: bundlePrices.currencyCode,
          imageUrl: thumbnailImage?.url,
          compareAtPrice: bundlePrices.compareAtPrice,
        },
      });
    }
  }, [shopifyData, bundlePrices]);

  return (
    <>
      <section>
        <Breadcrumbs locale={locale} data={data} />

        <div className={styles.productPage}>
          <ProductGallery
            thumbnail={thumbnailImage}
            productMedia={productMedia}
            productData={{
              ...data,
              shopifyData,
            }}
          />
          <div className={styles.productInfo}>
            <ProductBundleInfo
              locale={locale}
              bundlePrices={bundlePrices}
              shopifyData={shopifyData}
              productBundleData={data}
            />
            <div className={styles.topDivider} />
            <div className={styles.productExpanders}>
              {data.ingredients && (
                <Expander title={ingredientsTitle}>
                  <RichText text={data.ingredients.json} />
                </Expander>
              )}

              {data.directionOfUse && (
                <Expander title={directionOfUseTitle}>
                  <RichText text={data.directionOfUse.json} />
                </Expander>
              )}

              {data.expandersCollection.items.map(expander => (
                <Expander title={expander.title}>
                  <RichText text={expander.content.json} />
                </Expander>
              ))}
            </div>
            {disclaimerText && (
              <RichText className={styles.disclaimerText} text={disclaimerText.json} />
            )}
            <div className={styles.bottomDivider} />
          </div>
        </div>
        {reviewProducts.length === 1 && (
          <EvesReviews
            eanNumber={reviewProducts[0]?.shopifyData?.variants?.[0]?.sku}
            locale={locale}
            title={reviewsTitle}
          />
        )}
        {renderSections('', locale, otherSections, undefined)}
        <RelatedProducts
          productId={shopifyData?.variants?.[0]?.product_id}
          data={relatedProductsSection}
          locale={locale}
          className={styles.relatedProductsSection}
        />
      </section>
    </>
  );
};

export default PageProductBundle;
